import { formatMoney } from "@shopify/theme-currency";
import storefront from "../helpers/storefront";
import * as queries from "../helpers/storefront/queries";
import { gsap } from "gsap/dist/gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import getSellingPlanId from "../helpers/getSellingPlanId";

gsap.registerPlugin(ScrollTrigger);

export default class ProductForm {
  constructor({ el, data }) {
    // Data set
    this.data = data;
    this.id = data.id;
    this.product = data.product;
    this.productsRelated = data.productsRelated;
    this.productsBundle = data.productsBundle;
    this.variants = data.product.variants;
    this.initialId = null;
    this.currentVariant = this.getInitialVariant();
    this.currentProduct = null;

    // Element set
    this.el = el;
    this.price = this.el.querySelectorAll("[data-price]");
    this.addMessage = this.el.querySelectorAll("[data-add-message]");
    this.priceRange = this.el.querySelector("[data-product-price]");
    this.productTitle = this.el.querySelector("[data-product-title]");
    this.form = this.el.querySelector("[data-product-form]");
    this.addButton = this.el.querySelector("[data-product-add]");
    this.addBundle = this.el.querySelector("[data-bundle-add]");
    this.breadPay = this.el.querySelector("#bread-checkout-btn-product");
    this.breadPayDupe = this.el.querySelector("[data-bread-pay]");
    this.cartSection = document.querySelector("section-cart");

    this.input = this.el.querySelector("[data-variant-picker]");
    this.optionName = this.el.querySelector("[data-option-name]");
    this.productGroupContainer = this.el.querySelector("[data-product-group]");
    this.productGroup = this.product.tags?.find((t) => t.includes("group:"));
    this.group = this.productGroup?.split(":")[2];
    this.groupItems = null;
    this.groupUrl = null;
    this.products = this.productGroup?.split(":")[2];
    this.miniCart = document.querySelector("[data-cart]");
    this.miniCartTrigger = this.el.querySelectorAll("[data-cart-toggle]");

    // Related Products
    this.relatedProducts = this.el.querySelectorAll("[data-related-product]");
    this.relatedContainer = this.el.querySelector("[data-related-products]");
    this.relatedContainerTrigger = this.el.querySelectorAll(
      "[data-related-products-toggle]"
    );

    // Bundle Products
    this.bSelectors = this.el.querySelectorAll(
      "[data-related-bundles] [data-bundle-product]"
    );
    this.bundleProducts = [];
    this.bundleContainerTrigger = this.el.querySelectorAll(
      "[data-related-bundles-toggle]"
    );
    this.variantInputs = this.el.querySelectorAll("[data-variant-input]");
    this.bundleDiscount = this.product.compare_at_price
      ? this.product.compare_at_price - this.product.price
      : 0;

    // Setup
    this.addItemToCart = this.addItemToCart.bind(this);
    this.addBundleToCart = this.addBundleToCart.bind(this);
    this.handleSidebar = this.handleSidebar.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onRelatedVariantChange = this.onRelatedVariantChange.bind(this);
    this.onBundleVariantChange = this.onBundleVariantChange.bind(this);

    this.init();
    this.getFullProductData();

    document.addEventListener("refreshComponents", (e) => {
      setTimeout(() => {
        this.relatedContainerTrigger = this.el.querySelectorAll(
          "[data-related-products-toggle]"
        );
        this.miniCartTrigger = this.el.querySelectorAll("[data-cart-toggle]");
        this.bundleContainerTrigger = this.el.querySelectorAll(
          "[data-related-bundles-toggle]"
        );
        this.handleSidebar();
      }, 500);
    });
  }

  async getFullProductData() {
    const response = await storefront.request({
      query: queries.product,
      variables: {
        handle: this.product.handle,
      },
    });

    if (!response?.data?.productByHandle) return;
    this.currentProduct = response.data?.productByHandle;
  }

  init() {
    this.handleSidebar();
    this.form?.addEventListener("submit", (e) => {
      e.preventDefault();
      this.bSelectors = this.el.querySelectorAll(
        "[data-related-bundles] [data-bundle-product]"
      );
      this.cSelectors = this.el.querySelectorAll(
        "[data-bundle-configuration-input]"
      );
      let inputsArray;
      if (this.cSelectors?.length) {
        inputsArray = Array.from(this.cSelectors).filter((item) => {
          return item.value.length > 0;
        });
      }

      if (e.submitter.getAttribute("name") === "addBundle") {
        this.addBundleToCart(e);
      } else if (inputsArray?.length) {
        this.addBundleItemsToCart(inputsArray);
      } else {
        this.addItemToCart();
      }
    });
    this.input?.addEventListener("change", this.onVariantChange);
    this.el.querySelectorAll("[add-related-bundle]").forEach((atc) => {
      atc.addEventListener("click", (e) => this.addBundleToCart(e, atc));
    });
    this.el.querySelectorAll("[add-to-cart]").forEach((atc) => {
      atc.addEventListener("click", (e) => this.handleRelatedItemCart(e, atc));
    });
    this.relatedProducts?.forEach((product) => {
      product.addEventListener("change", this.onRelatedVariantChange);
    });

    this.bSelectors?.forEach((product) => {
      product.addEventListener("change", (e) => {
        this.onBundleVariantChange(e);
      });
    });
    this.bSelectors.length && this.onBundleVariantChange();
  }

  handleSidebar() {
    this.miniCartTrigger.forEach((trigger) => {
      trigger.addEventListener("click", () => {
        document.dispatchEvent(new CustomEvent("mini-cart:open"));
      });
    });
    this.relatedContainer?.classList.add("loaded");
    this.relatedContainerTrigger.forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        let opened = this.relatedContainer?.classList.contains("is-opened");
        this.relatedContainer?.classList[opened ? "remove" : "add"](
          "is-opened"
        );
        document.body.classList[opened ? "remove" : "add"]("overflow-hidden");

        document.documentElement.classList[opened ? "remove" : "add"](
          "overflow-hidden"
        );
      });
    });
  }

  async handleRelatedItemCart(e, atc) {
    e.preventDefault();
    let remove = atc.classList.contains("remove") ? true : false,
      variant = atc.getAttribute("data-variant"),
      updates = { [variant]: 0 },
      v = null;

    const response = await storefront.request({
      query: queries.variant,
      variables: {
        ids: encodeId(variant, "ProductVariant"),
      },
    });

    if (response) {
      v = response?.data?.nodes[0];
    }

    if (!v) return;

    const inventoryReno = v.inventoryReno?.value
      ? parseInt(v.inventoryReno.value)
      : null;

    const inventoryAtlanta = v.inventoryAtlanta?.value
      ? parseInt(v.inventoryAtlanta.value)
      : null;

    const properties = {
      _inventoryReno: inventoryReno,
      _inventoryAtlanta: inventoryAtlanta,
    };

    let payload = {
      id: variant,
      quantity: 1,
      properties: properties,
    },
      action = {
        onAfterAdd: () => {
          atc.classList[remove ? "remove" : "add"]("remove");
          atc
            .closest(".product-form__option")
            .classList[remove ? "remove" : "add"]("selected");
        },
      };

    !remove && window.addToCart(payload, null, action);
    remove && window.updateCart(updates, null, action);
  }

  async addBundleItemsToCart(inputsArray) {
    let vp =
      this.product?.variants?.length < 2 && this.currentProduct
        ? this.currentProduct.variants.edges[0].node
        : this.currentVariant;
    if (!vp.id) return;

    console.log(`inputsArray`, inputsArray);
    // Sometimes this data comes from graphQL, sometimes from liquid, so variant ID is something a number and sometimes a GID 😭
    const mainVariantId = typeof vp.id === 'string' && vp.id.includes('gid://shopify') ? vp.id : encodeId(vp.id, "ProductVariant");
    inputsArray = inputsArray.map((item) => ({
      id: parseInt(item.value),
      quantity: isFinite(parseInt(item.dataset.quantity)) ? parseInt(item.dataset.quantity) : 1
    }));

    console.log(`inputsArray after map`, inputsArray);

    let ids = inputsArray.map(({ id }) => encodeId(id, "ProductVariant"));
    ids.unshift(mainVariantId);

    const response = await storefront.request({
      query: queries.variant,
      variables: {
        ids: ids,
      },
    });

    let v,
      title = "Include These Popular Add Ons";

    if (response) {
      v = response?.data?.nodes;
    }

    if (!v) return;

    let items = v.map((item) => {
      const id = item.id.split("/").pop();

      const quantity = inputsArray.find((bundleItem) => {
        return bundleItem.id === parseInt(id);
      })?.quantity || 1;

      const inventoryReno = item.inventoryReno?.value
        ? parseInt(item.inventoryReno.value)
        : null;

      const inventoryAtlanta = item.inventoryAtlanta?.value
        ? parseInt(item.inventoryAtlanta.value)
        : null;

      return {
        id,
        quantity,
        properties: {
          _inventoryReno: inventoryReno,
          _inventoryAtlanta: inventoryAtlanta,
          _selling_plan: getSellingPlanId(item),
          _added_by: 'PDP addBundleItemsToCart',
        },
        selling_plan: getSellingPlanId(item),
      };
    });

    items = items.reverse();
    window.addToCart(items);
  }

  async addBundleToCart(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    let relatedBundleParent = e?.target?.closest("[data-related-bundle]"),
      relatedBundleProducts =
        relatedBundleParent?.previousSibling.previousSibling.innerHTML,
      parent = relatedBundleParent ? relatedBundleParent : this.el;
    this.variantInputs = parent.querySelectorAll("[data-variant-input]");
    this.product = relatedBundleParent
      ? JSON.parse(relatedBundleProducts).product
      : this.product;

    let v;
    let inputsArray = Array.from(this.variantInputs).map((item) =>
      parseInt(item.value)
    );
    let ids = inputsArray.map((input) => encodeId(input, "ProductVariant"));
    const response = await storefront.request({
      query: queries.variant,
      variables: {
        ids: ids,
      },
    });

    if (response) {
      v = response?.data?.nodes;
    }
    if (!v) return;
    const bundleId = inputsArray.map((input) => input).join("/");
    const { title, featured_image } = this.product;
    const qty = 1;
    const titles = v.map((vp) => {
      return `${vp.product.title}${vp.title != "Default Title" ? `: ${vp.title}` : ""
        }`;
    });
    const items = v.map((vp) => {
      const inventoryReno = vp.inventoryReno?.value
        ? parseInt(vp.inventoryReno.value)
        : null;
      const inventoryAtlanta = vp.inventoryAtlanta?.value
        ? parseInt(vp.inventoryAtlanta.value)
        : null;

      return {
        id: vp.id.split("/").pop(),
        qty,
        selling_plan: getSellingPlanId(vp),
        properties: {
          _inventoryReno: inventoryReno,
          _inventoryAtlanta: inventoryAtlanta,
          _bundle: this.product.handle,
          _bundleId: bundleId,
          _bundlePrice: this.bundlePrice,
          _originalPrice: this.originalPrice,
          _bundleTitles: titles.join("+"),
          _bundleTitle: title,
          _bundleImage: featured_image,
          _bundleQuantity: qty,
          _selling_plan: getSellingPlanId(vp),
          _added_by: 'PDP addBundleToCart',
        },
      };
    });

    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    if (params.p) {
      let updates = {
        [params.p]: 0,
      },
        sections = "main-cart,header";
      window.updateCart(updates, sections, {
        onAfterAdd: () => {
          window.addToCart(items);
        },
      });
      params.delete("p");
    } else {
      window.addToCart(items);
    }
  }

  async addItemToCart() {
    let vp =
      this.product?.variants?.length < 2 && this.currentProduct
        ? this.currentProduct.variants.edges[0].node
        : this.currentVariant;

    if (!vp.id) return;
    const response = await storefront.request({
      query: queries.variant,
      variables: {
        ids: encodeId(vp.id, "ProductVariant"),
      },
    });

    if (response)
      vp = response?.data?.nodes
        ? response?.data?.nodes?.filter((p) => p)[0]
        : vp;

    let id = vp?.id.toString().includes("gid") ? vp?.id.split("/") : vp?.id;
    id = id.length > 1 ? id[id.length - 1] : id;

    const inventoryReno = vp.inventoryReno?.value
      ? parseInt(vp.inventoryReno.value)
      : null;
    const inventoryAtlanta = vp.inventoryAtlanta?.value
      ? parseInt(vp.inventoryAtlanta.value)
      : null;

    const properties = {
      _inventoryReno: inventoryReno,
      _inventoryAtlanta: inventoryAtlanta,
      _selling_plan: this.form.elements.selling_plan?.value,
      _added_by: 'PDP addItemToCart',
    };

    window.addToCart({
      id: this.variants ? id : this.product.id,
      quantity: 1,
      properties,
      selling_plan: this.form.elements.selling_plan?.value
    });
  }

  updateValue(option, type, value) {
    this.el.querySelectorAll(`[${option}]`).forEach((item) => {
      item[type] = value;
    });
  }

  /**
   * Get initial variant based on URL params
   * @returns variant object
   */

  getInitialVariant() {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let foundVariant = false;

    if (params.variant) {
      this.initialId = params.variant;
      let variants = this.products?.length ? this.products : this.variants;
      foundVariant = variants.find(
        (variant) => variant.id.toString().indexOf(params.variant) > 0
      );
    }

    if (foundVariant) return foundVariant;
    this.initialId = this.variants[0].id;
    return this.variants[0];
  }

  /**
   * Update hidden variant input
   */

  updateVariantInput() {
    const input = this.form?.querySelector('input[name="id"]');
    input.value = this.currentVariant?.id;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  /**
   * Update URL param with current variant ID
   */
  updateURL() {
    if (!this.currentVariant) return;
    let id = this.currentVariant?.id.toString().includes("gid")
      ? this.currentVariant?.id.split("/")
      : this.currentVariant?.id;
    id = id.length > 1 ? id[id.length - 1] : id;
    let url = this.groupUrl
      ? window.location.pathname.replace(/\/[^\/]*$/, `/${this.groupUrl}`)
      : `${window.location.pathname}?variant=${id}`;
    window.history.replaceState({}, "", url);
  }

  onBundleVariantChange(e) {
    let relatedBundleParent = e?.target.closest("[data-related-bundles]");
    relatedBundleParent = relatedBundleParent
      ? relatedBundleParent
      : this.el.querySelector("[data-related-bundles]");

    let relatedBundleProducts = relatedBundleParent?.querySelector(
      "[data-bundle-products]"
    ).innerHTML,
      parent = relatedBundleParent ? relatedBundleParent : this.el;
    if (!parent) return;
    this.productsBundle = relatedBundleParent
      ? JSON.parse(relatedBundleProducts).productsBundle
      : this.productsBundle;
    this.bSelectors = parent.querySelectorAll("[data-bundle-product]");
    this.bundleProducts = [];
    this.bSelectors?.forEach((p) => {
      let id = p.getAttribute("data-bundle-product").split("/").pop(),
        cp = this.productsBundle?.find((r) => r.id.toString() === id),
        fieldsets = Array.from(p.querySelectorAll(".product-bundle__options"));
      let o = fieldsets.map((fieldset) => {
        let inputs = Array.from(fieldset.querySelectorAll("input"));
        return inputs.find((radio) => radio.checked)?.value;
      });
      if (!cp) return;
      let prod = cp.variants.find((variant) => {
        let options = variant.options
          ? variant.options
          : variant.selectedOptions.map((o) => o.value);
        return !options
          .map((option, index) => {
            return o[index] === option;
          })
          .includes(false);
      });
      this.bundleProducts.push(prod ? prod : cp);

      // Adjust Variant ID
      p.querySelector("[data-variant-input]").value = prod?.id
        ? prod?.id
        : p.querySelector("[data-variant-input]").value;
    });

    this.handleBundlePricing(e);
  }

  handleBundlePricing(e) {
    let relatedBundleParent = e?.target.closest("[data-related-bundles]");
    relatedBundleParent = relatedBundleParent
      ? relatedBundleParent
      : this.el.querySelector("[data-related-bundles]");
    let parent = relatedBundleParent ? relatedBundleParent : this.el;
    let total_price = 0;
    if (!this.bundleProducts.length) return;
    this.bundleProducts.map((p) => {
      total_price += parseInt(p.price);
    });
    let d = this.bundleDiscount;
    const priceValue = window.formatMoneyWithoutConversion(
      (total_price - d) / 100
    );
    let p = `<span class="price__regular " >${priceValue}</span>`;
    this.originalPrice = Math.max(this.product.compare_at_price_max, this.product.price, total_price);

    let c =
      total_price - d < this.originalPrice
        ? `<del class="price__compare">${window.formatMoneyWithoutConversion(
          this.originalPrice / 100
        )}</del>`
        : "";

    this.bundlePrice = total_price - d;
    let priceRange = parent.querySelector("[data-product-price]");

    // Update payment terms amount to reflect actual bundle price. This is not documented
    // so might break at any time, but seems to work reliably for now.
    try {
      parent.querySelectorAll('shopify-payment-terms').forEach(paymentTerms => {
        const meta = JSON.parse(paymentTerms.getAttribute('shopify-meta'));
        meta.variants[0].full_price = priceValue;
        paymentTerms.setAttribute('shopify-meta', JSON.stringify(meta));
      });
    } catch (error) {
      console.warn('Unable to update payment terms price', error);
      parent.querySelectorAll('shopify-payment-terms').forEach(paymentTerms => paymentTerms.remove());
    }

    parent.querySelectorAll("[data-price]").forEach((price) => {

      if (price.innerText === priceValue) return;
      price.innerHTML = p;
      parent.querySelector("#bread-checkout-btn-product")?.remove();
    });
    parent.querySelectorAll("item-count").forEach((el) => {
      el.setAttribute('count', this.bundleProducts.length);
    });
    if (!priceRange) return;
    priceRange.innerHTML = p + c;
  }
  onRelatedVariantChange() {
    this.relatedProducts?.forEach((p) => {
      let id = p.getAttribute("data-related-product"),
        cp = this.productsRelated?.find((r) => r.id.toString() === id),
        fieldsets = Array.from(p.querySelectorAll(".product-related__options"));
      let o = fieldsets.map((fieldset) => {
        let inputs = Array.from(fieldset.querySelectorAll("input"));
        return inputs.find((radio) => radio.checked)?.value;
      });
      if (!cp) return;
      let prod = cp.variants.find((variant) => {
        let options = variant.options
          ? variant.options
          : variant.selectedOptions.map((o) => o.value);
        return !options
          .map((option, index) => {
            return o[index] === option;
          })
          .includes(false);
      });

      if (!prod) return;
      p.querySelector("[data-related-price]").innerHTML = window.formatMoney(
        prod.price / 100
      );
      p.querySelector("[add-to-cart]").setAttribute("data-variant", prod.id);
    });
  }

  /**
   * On variant change
   */
  onVariantChange() {
    this.updateOptions();
    this.updateVariant();
    this.updateURL();
    this.updateVariantInput();
    this.renderProductInfo();
  }

  updateOptions() {
    const fieldsets = Array.from(
      this.el.querySelectorAll(".product-form__options")
    );
    this.options = fieldsets.map((fieldset) => {
      let inputs = Array.from(fieldset.querySelectorAll("input"));
      this.groupUrl = inputs
        .find((radio) => radio.checked)
        ?.getAttribute("data-product");
      return inputs.find((radio) => radio.checked)?.value;
    });
  }

  /**
   * Update current variant based on current option array
   */
  updateVariant() {
    let variants = this.products?.length ? this.products : this.variants;
    this.currentVariant = variants.find((variant) => {
      let options = variant.options
        ? variant.options
        : variant.selectedOptions.map((o) => o.value);
      return !options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  /**
   * Get section data based on new variant selection for rebuild markup
   */
  renderProductInfo() {
    let image = this.currentVariant?.featured_image
      ? this.currentVariant?.featured_image
      : this.currentVariant?.image;
    this.updateValue("data-variant-image", "src", image?.src);
    this.updateValue("data-variant-image", "srcset", "");

    window.handleConfigPricing(this.currentVariant, this);

    let currentProduct;

    if (this.groupItems) {
      currentProduct = this.groupItems?.find((i) => i.handle === this.groupUrl);
    }
    const productUpdated = new CustomEvent("productUpdate", {
      detail: {
        ...(currentProduct && { product: currentProduct }),
        variant: this.currentVariant,
      },
    });
    document.dispatchEvent(productUpdated);

    let cv = currentProduct.variants.edges.find(
      (v) => v.node.title === this.currentVariant.title
    )?.node?.preorder?.value;

    this.el.querySelectorAll("[data-add-message]").forEach((message) => {
      message.innerHTML = cv ? "Pre-Order" : "Add to Cart";
    });
  }
}
